import React, { useState } from 'react';
import {
  ChevronLeft,
  Sparkles,
  MapPin,
  Zap,
  ArrowRight,
  Award,
  Map as MapIcon
} from 'lucide-react';

interface AdminGoldenTargetPageProps {
  onBackToHome?: () => void;
  onBackToPortal: () => void;
  onNavigateSubpage: (page: 'admin-portal' | 'admin-proposal' | 'admin-diagnostic' | 'admin-golden-targets' | 'admin-map') => void;
}

interface GoldenTargetItem {
  id: string;
  rank: number;
  name: string;
  region: string;
  location: string;
  substation: string;
  feeder: string;
  remainingKw: number;
  estimatedRoofPyung: number;
  potentialMw: number;
  factoryCount: number;
  buildingType: string;
  salesPitch: string;
  keyAdvantage: string;
  recommendedAction: string;
}

export const AdminGoldenTargetPage: React.FC<AdminGoldenTargetPageProps> = ({
  onBackToHome,
  onBackToPortal,
  onNavigateSubpage,
}) => {
  const [selectedFilter, setSelectedFilter] = useState<'ALL' | '수도권' | '강원' | '충청·영남·호남'>('ALL');

  const goldenTargets: GoldenTargetItem[] = [
    {
      id: 'target-pocheon',
      rank: 1,
      name: '포천 가구단지 & 용정일반산단',
      region: '수도권',
      location: '경기도 포천시 군내면·가산면 일대',
      substation: '154kV 포천변전소',
      feeder: '군내 D/L, 가산 D/L',
      remainingKw: 4100,
      estimatedRoofPyung: 110000,
      potentialMw: 55.0,
      factoryCount: 95,
      buildingType: '노후 섬유·가구 판넬 공장',
      salesPitch: '"공장 지붕 수리비 및 방수공사 전액 지원해 드리고, 20년간 연 수천만 원 임대료 꼬박꼬박 받으세요."',
      keyAdvantage: '노후 판넬 지붕이 많아 수리비 지원 혜택 제안 시 계약 성공률 최상위 권역.',
      recommendedAction: '가산면 가구거리 및 용정산단 공장 대표 1:1 방문 미팅 추천',
    },
    {
      id: 'target-icheon',
      rank: 2,
      name: '이천 모가·부발 물류센터 클러스터',
      region: '수도권',
      location: '경기도 이천시 부발읍·모가면 일대',
      substation: '154kV 이천변전소',
      feeder: '부발 D/L, 모가 D/L',
      remainingKw: 3200,
      estimatedRoofPyung: 180000,
      potentialMw: 90.0,
      factoryCount: 42,
      buildingType: '대형 복합물류센터 & 냉동창고',
      salesPitch: '"창고 지붕 유휴공간으로 20년 확정 임대수익 수억 원을 자부담 0원으로 확보하세요."',
      keyAdvantage: '건당 500kW~1.5MW급 초대형 계약 체결 가능. 한전 선로 3.2MW 안정적.',
      recommendedAction: '물류창고 운영 총괄이사 및 자산관리사 대상 기업형 제안서 발송',
    },
    {
      id: 'target-wonju',
      rank: 3,
      name: '원주 문막일반산단 & 자동차부품밸리',
      region: '강원',
      location: '강원특별자치도 원주시 문막읍 건등리 일대',
      substation: '154kV 원주변전소',
      feeder: '문막 D/L, 호저 D/L',
      remainingKw: 5200,
      estimatedRoofPyung: 150000,
      potentialMw: 75.0,
      factoryCount: 88,
      buildingType: '자동차부품·기계제조 공장',
      salesPitch: '"원주 최고 여유용량 5MW 확보 완료! 인허가부터 시공까지 초고속 착공 보증."',
      keyAdvantage: '전국 최고 수준의 5.2MW 잔여용량으로 인입 지연 리스크 전혀 없음.',
      recommendedAction: '문막산단 관리사무소 방문 및 입주기업 협의회 대상 설명회 연계',
    },
    {
      id: 'target-seoul',
      rank: 4,
      name: '서울 G밸리 1·2·3단지 & 동남권물류단지',
      region: '수도권',
      location: '서울특별시 금천구 가산동 / 송파구 장지동',
      substation: '154kV 가산/문정변전소',
      feeder: '디지털 D/L, 물류 D/L',
      remainingKw: 5400,
      estimatedRoofPyung: 220000,
      potentialMw: 110.0,
      factoryCount: 450,
      buildingType: '아파트형공장 지식산업센터 & 물류단지',
      salesPitch: '"도심 RE100 자가소비형 PPA 전환으로 기업 ESG 평가 최고등급 획득."',
      keyAdvantage: '도심 전력 부하가 높아 5.4MW 여유 넉넉. 서울 최상위 랜드마크 사이트.',
      recommendedAction: '지식산업센터 입주자대표회의(관리단) 안건 상정 추진',
    },
    {
      id: 'target-gumi',
      rank: 5,
      name: '구미 국가산단 4단지 & 하이테크밸리',
      region: '충청·영남·호남',
      location: '경상북도 구미시 산동읍 봉산리 일대',
      substation: '154kV 옥계변전소',
      feeder: '산동 D/L, 옥계 D/L',
      remainingKw: 3800,
      estimatedRoofPyung: 380000,
      potentialMw: 190.0,
      factoryCount: 175,
      buildingType: '대형 전자·반도체 협력공장',
      salesPitch: '"경북 최대 38만 평 지붕 타깃! RE100 기업 전용 지붕 임대 장기 계약."',
      keyAdvantage: '초대형 공장 지붕 면적으로 1개 사이트당 1MW+ 대규모 수주 가능.',
      recommendedAction: '구미 4·5단지 공장 대표자 협의회 미팅 추천',
    },
    {
      id: 'target-hoengseong',
      rank: 6,
      name: '횡성 한우대형축사 & 우천농공단지',
      region: '강원',
      location: '강원특별자치도 횡성군 우천면·둔내면',
      substation: '154kV 횡성변전소',
      feeder: '우천 D/L, 둔내 D/L',
      remainingKw: 3300,
      estimatedRoofPyung: 120000,
      potentialMw: 60.0,
      factoryCount: 60,
      buildingType: '대형 한우축사 & 도축가공공장',
      salesPitch: '"축사 지붕 차광 효과로 여름철 가축 폐사율 급감 + 20년 확정 연금 수익."',
      keyAdvantage: '축사 내부 온도 3~4도 저감 효과로 축산 농가 설득력 극대화.',
      recommendedAction: '횡성 한우협회 및 우천면 축산계 거점 순회 상담',
    },
    {
      id: 'target-chungju',
      rank: 7,
      name: '충주 첨단바이오산단 & 서충주공장벨트',
      region: '충청·영남·호남',
      location: '충청북도 충주시 주덕읍·대소원면 일대',
      substation: '154kV 충주변전소',
      feeder: '주덕 D/L, 대소원 D/L',
      remainingKw: 4200,
      estimatedRoofPyung: 130000,
      potentialMw: 65.0,
      factoryCount: 75,
      buildingType: '바이오·식품·정밀화학 공장',
      salesPitch: '"충북권 최고의 4.2MW 여유선로 확보! 지붕 보수비 전액 지원."',
      keyAdvantage: '충주 첨단산단 신축·증축 공장 다수 분포.',
      recommendedAction: '서충주 기업도시 및 주덕읍 공장 라인 집중 공략',
    },
  ];

  const filteredTargets = goldenTargets.filter((t) => {
    if (selectedFilter === 'ALL') return true;
    return t.region === selectedFilter;
  });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-sky-500 selection:text-white flex flex-col">
      
      {/* 1. Master Top Bar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 py-3 px-4 sm:px-6 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          
          <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
            <button
              type="button"
              onClick={onBackToPortal}
              className="flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-2 rounded-xl transition-all cursor-pointer shadow-sm"
            >
              <ChevronLeft className="w-4 h-4 text-sky-600" />
              <span>포털 메인으로</span>
            </button>

            {onBackToHome && (
              <button
                type="button"
                onClick={onBackToHome}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 bg-white hover:bg-slate-100 border border-slate-200 px-3 py-2 rounded-xl transition-all cursor-pointer shadow-xs"
              >
                <span>메인 홈</span>
              </button>
            )}

            <div className="flex items-center gap-2 ml-1">
              <span className="text-sm font-black text-slate-900 tracking-tight">
                🎯 영업 추천 지역
              </span>
            </div>
          </div>

          {/* Quick Subpage Switcher Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto text-xs">
            <button
              onClick={() => onNavigateSubpage('admin-proposal')}
              className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 font-bold cursor-pointer transition-colors"
            >
              1. 간단 견적서 만들기
            </button>
            <button
              onClick={() => onNavigateSubpage('admin-diagnostic')}
              className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 font-bold cursor-pointer transition-colors"
            >
              2. 계통 여부 확인
            </button>
            <button
              onClick={() => onNavigateSubpage('admin-golden-targets')}
              className="px-3 py-1.5 rounded-xl bg-amber-400 text-slate-950 font-black cursor-pointer shadow-sm"
            >
              3. 영업 추천 지역
            </button>
            <button
              onClick={() => onNavigateSubpage('admin-map')}
              className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 font-bold cursor-pointer transition-colors"
            >
              4. GIS 관제 맵
            </button>
          </div>

        </div>
      </header>

      {/* 2. Top Summary & Filter Header */}
      <section className="bg-gradient-to-r from-amber-50 via-white to-slate-50 border-b border-slate-200 px-4 sm:px-6 py-6">
        <div className="max-w-7xl mx-auto space-y-4">
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-xs font-black mb-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                <span>한전 선로용량 3MW+ 여유 &amp; 지붕 밀집 교집합 엄선</span>
              </div>
              <h1 className="text-xl sm:text-2xl font-black text-slate-900">
                영업 추천 지역
              </h1>
              <p className="text-xs text-slate-600 mt-0.5">
                선로 포화 지역을 피해, 인입 여유가 확실하고 노후 지붕 수리비 니즈가 높은 알짜배기 황금 산단을 공략하세요.
              </p>
            </div>

            {/* Region Filter Chips */}
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              {[
                { key: 'ALL', label: '전체 (7개 황금 권역)' },
                { key: '수도권', label: '수도권 (포천/이천/서울)' },
                { key: '강원', label: '강원권 (원주/횡성)' },
                { key: '충청·영남·호남', label: '지방권 (구미/충주)' },
              ].map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setSelectedFilter(f.key as any)}
                  className={`px-3 py-1.5 rounded-xl font-bold text-xs transition-all cursor-pointer ${
                    selectedFilter === f.key
                      ? 'bg-amber-400 text-slate-950 shadow font-black'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 3. Golden Target Cards Grid */}
      <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 w-full flex-1 space-y-6">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredTargets.map((target) => (
            <div
              key={target.id}
              className="bg-white border border-slate-200/90 hover:border-amber-400 rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all space-y-4 flex flex-col justify-between"
            >
              <div className="space-y-3.5">
                
                {/* Header Badge */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-xl bg-amber-400 text-slate-950 flex items-center justify-center text-sm font-black shadow-sm">
                      #{target.rank}
                    </span>
                    <div>
                      <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider block">
                        {target.region} • {target.buildingType}
                      </span>
                      <h2 className="text-lg sm:text-xl font-black text-slate-900">{target.name}</h2>
                    </div>
                  </div>

                  <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-black flex items-center gap-1 border border-emerald-200">
                    <Zap className="w-3.5 h-3.5" />
                    <span>여유 {(target.remainingKw / 1000).toFixed(1)}MW</span>
                  </span>
                </div>

                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  <span>{target.location} ({target.substation} / {target.feeder})</span>
                </p>

                {/* Target Metric Stats */}
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                    <span className="text-slate-500 block text-[10px] font-bold">총 지붕 면적</span>
                    <strong className="text-amber-600 font-black text-sm">{(target.estimatedRoofPyung / 10000).toFixed(1)}만 평</strong>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                    <span className="text-slate-500 block text-[10px] font-bold">잠재 발전 용량</span>
                    <strong className="text-sky-600 font-black text-sm">{target.potentialMw} MW</strong>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                    <span className="text-slate-500 block text-[10px] font-bold">입주 공장 수</span>
                    <strong className="text-slate-900 font-black text-sm">{target.factoryCount}개 동</strong>
                  </div>
                </div>

                {/* Pitch Script & Core Advantage */}
                <div className="bg-amber-50/50 rounded-2xl p-3.5 border border-amber-200/70 text-xs space-y-1.5">
                  <span className="text-amber-800 font-black flex items-center gap-1 text-[11px]">
                    <Award className="w-3.5 h-3.5" />
                    <span>현장 사장님 클로징 스크립트:</span>
                  </span>
                  <p className="text-slate-800 font-bold italic leading-relaxed">
                    {target.salesPitch}
                  </p>
                  <p className="text-[11px] text-slate-600 pt-1 border-t border-amber-200/50">
                    💡 <strong className="text-slate-800">핵심 강점:</strong> {target.keyAdvantage}
                  </p>
                </div>

              </div>

              {/* Action Button Row */}
              <div className="pt-2 flex flex-col sm:flex-row items-center gap-2">
                <button
                  onClick={() => onNavigateSubpage('admin-proposal')}
                  className="w-full sm:flex-1 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs flex items-center justify-center gap-1.5 transition-all shadow-sm cursor-pointer"
                >
                  <span>이 지역으로 견적서 작성</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={() => onNavigateSubpage('admin-map')}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer transition-colors"
                >
                  <MapIcon className="w-3.5 h-3.5 text-sky-600" />
                  <span>GIS 지도 보기</span>
                </button>
              </div>

            </div>
          ))}
        </div>

      </main>

    </div>
  );
};
